import { isEmpty } from 'lodash';
import Constants from '../../Constants';
import * as actionTypes from '../../constants/actionTypes';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { isStateStale } from '../../lib/StateUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { docSchema, schemaSpecSchema } from '../../schemas';
import { setTotalItems, setTotalPages } from '../app';

export const getSchemaSpec =
  (
    id,
    { aclQueryMode, ignoreErrors } = {
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
      ignoreErrors: false,
    },
  ) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_SCHEMA_SPEC_REQUEST,
          actionTypes.GET_SCHEMA_SPEC_SUCCESS,
          actionTypes.GET_SCHEMA_SPEC_FAILURE,
        ],
        endpoint: `/data_schemas/${id}`,
        schema: schemaSpecSchema,
        requestMethod: GET,
        aclQueryMode,
        ignoreErrors,
        payload: {
          template: 1,
        },
      },
    });
  };

export const getAllSchemaSpecs =
  (aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key) =>
  (dispatch, getState) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_SCHEMA_SPECS_REQUEST,
          actionTypes.GET_SCHEMA_SPECS_SUCCESS,
          actionTypes.GET_SCHEMA_SPECS_FAILURE,
        ],
        endpoint: `/data_schemas`,
        schema: [schemaSpecSchema],
        requestMethod: GET,
        aclQueryMode,
        payload: { template: 1 },
      },
    }).then(() => {
      if (isStateStale(getState().schemaSpecs.lastUpdatedPublic)) {
        return dispatch({
          [CALL_PRIVATE_API]: {
            types: [
              actionTypes.GET_PUBLIC_SCHEMA_SPECS_REQUEST,
              actionTypes.GET_PUBLIC_SCHEMA_SPECS_SUCCESS,
              actionTypes.GET_PUBLIC_SCHEMA_SPECS_FAILURE,
            ],
            endpoint: `/data_schemas/public`,
            schema: [schemaSpecSchema],
            requestMethod: GET,
            aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
          },
        });
      }
    });
  };

export const getSchemaSpecsListing =
  (page, sortCriteria, availableFilters) => (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
    } = getState();

    const aclQueryMode = accessControl.mode ?? ACLQueryModeTypes.MODE_OWNER.key;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      filters[SearchFilterViews.SCHEMA_SPECS],
      availableFilters,
    );
    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/data_schemas';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
      };
    } else {
      requestMethod = POST;
      endpoint = `/data_schemas/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_SCHEMA_SPEC_LISTING_REQUEST,
          actionTypes.GET_SCHEMA_SPEC_LISTING_SUCCESS,
          actionTypes.GET_SCHEMA_SPEC_LISTING_FAILURE,
        ],
        endpoint,
        payload,
        schema: [schemaSpecSchema],
        requestMethod,
        aclQueryMode,
        requestInfo: { view: SearchFilterViews.SCHEMA_SPECS },
      },
    }).then((response) => {
      dispatch(setTotalPages(response.totalPages));
      dispatch(setTotalItems(response.totalItems));

      return response;
    });
  };

export const getAllSchemaSpecsSearchSuggestions =
  (filters, availableFilters) => (dispatch, getState) => {
    const {
      app: { accessControl },
    } = getState();
    const aclQueryMode = accessControl.mode;

    const { sortKey, sortOrder } =
      Constants.SortCriteriaInfo[Constants.SortCriteria.LAST_CREATED_DESC];
    const { processedFilters } = processSearchFilters(filters, availableFilters);

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_SCHEMA_SPECS_REQUEST,
          actionTypes.SEARCH_SCHEMA_SPECS_SUCCESS,
          actionTypes.SEARCH_SCHEMA_SPECS_FAILURE,
        ],
        endpoint: `/data_schemas/search?${params}`,
        schema: [schemaSpecSchema],
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: { view: SearchFilterViews.SCHEMA_SPECS },
      },
    });
  };

export const createSchemaSpec =
  (payload = {}) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_SCHEMA_SPEC_REQUEST,
          actionTypes.CREATE_SCHEMA_SPEC_SUCCESS,
          actionTypes.CREATE_SCHEMA_SPEC_FAILURE,
        ],
        endpoint: `/data_schemas`,
        payload: {
          ...payload,
          template: true,
        },
        schema: schemaSpecSchema,
        requestMethod: POST,
      },
    });
  };

export const updateSchemaSpec =
  (id = null, payload = {}) =>
  (dispatch) => {
    console.log('updating spec');
    console.log(payload);
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_SCHEMA_SPEC_REQUEST,
          actionTypes.UPDATE_SCHEMA_SPEC_SUCCESS,
          actionTypes.UPDATE_SCHEMA_SPEC_FAILURE,
        ],
        endpoint: `/data_schemas/${id}`,
        payload,
        schema: schemaSpecSchema,
        requestMethod: PUT,
        requestInfo: {
          id,
        },
      },
    });
  };

export const deleteSchemaSpec =
  (id = null) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.DELETE_SCHEMA_SPEC_REQUEST,
          actionTypes.DELETE_SCHEMA_SPEC_SUCCESS,
          actionTypes.DELETE_SCHEMA_SPEC_FAILURE,
        ],
        endpoint: `/data_schemas/${id}`,
        schema: null,
        requestMethod: DELETE,
        requestInfo: {
          id,
        },
      },
    });
  };

export const fetchSchemaSpecDoc = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_SCHEMA_SPEC_DOC_REQUEST,
        actionTypes.GET_SCHEMA_SPEC_DOC_SUCCESS,
        actionTypes.GET_SCHEMA_SPEC_DOC_FAILURE,
      ],
      endpoint: `/data_schemas/${id}/docs`,
      schema: [docSchema],
      requestMethod: GET,
      payload: {
        expand: 1,
      },
      requestInfo: {
        id,
      },
    },
  });
};

export const updateSchemaSpecDoc = (id, content) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_SCHEMA_SPEC_DOC_REQUEST,
        actionTypes.UPDATE_SCHEMA_SPEC_DOC_SUCCESS,
        actionTypes.UPDATE_SCHEMA_SPEC_DOC_FAILURE,
      ],
      endpoint: `/data_schemas/${id}/docs?expand=1`,
      schema: [docSchema],
      requestMethod: POST,
      payload: {
        docs: [
          {
            name: `Doc for schema template ${id}`,
            description: `Doc for schema template ${id}`,
            doc_type: 'md',
            text: content,
          },
        ],
      },
      requestInfo: {
        id,
      },
    },
  });
};
